import React, { useEffect, useState } from "react";
import { CeoNav } from "../components/CeoNav";
import { api, formatErrorDetail } from "../lib/api";
import { ErrorState, TableSkeleton } from "../components/LoadState";
import { Plus, KeyRound, Trash2, UserCog } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle } from "../components/ui/dialog";

const empty = { name: "", email: "", password: "", role: "MANAGER" };

export default function UsersPage() {
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [resetting, setResetting] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/users");
      setUsers(data);
    } catch (e) {
      const msg = e.userMessage || "Failed to load users.";
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    try {
      await api.post("/users", form);
      toast.success("User created");
      setOpen(false); setForm(empty); load();
    } catch (e) {
      toast.error(e.userMessage || formatErrorDetail(e.response?.data?.detail) || "Failed");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this user?")) return;
    try { await api.delete(`/users/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e.userMessage || formatErrorDetail(e.response?.data?.detail) || "Failed"); }
  };

  const doReset = async () => {
    try {
      await api.patch(`/users/${resetting.id}`, { password: newPassword });
      toast.success("Password reset");
      setResetting(null); setNewPassword("");
    } catch (e) { toast.error(e.userMessage || "Failed"); }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="users-page">
      <CeoNav />
      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-display text-3xl font-bold">Managers &amp; Admins</h1>
            <div className="text-sm text-muted-foreground">Login users of LedgerFlow</div>
          </div>
          <button
            data-testid="user-new-btn"
            onClick={() => { setForm(empty); setOpen(true); }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:brightness-110 font-semibold text-sm"
          >
            <Plus size={16} /> New user
          </button>
        </div>

        {error ? <div className="mb-5"><ErrorState message={error} onRetry={load} /></div> : null}

        <div className="card-soft overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-muted-foreground text-xs uppercase tracking-wider">
                  <th className="px-5 py-3">Name</th>
                  <th className="px-5 py-3">Email</th>
                  <th className="px-5 py-3">Role</th>
                  <th className="px-5 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading ? <TableSkeleton rows={5} columns={4} /> : null}
                {!loading && users.map((u) => (
                  <tr key={u.id} className="border-t border-border/60" data-testid={`user-${u.id}`}>
                    <td className="px-5 py-3 font-semibold flex items-center gap-2">
                      <div className="w-8 h-8 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
                        <UserCog size={14} />
                      </div>
                      {u.name}
                    </td>
                    <td className="px-5 py-3 text-muted-foreground">{u.email}</td>
                    <td className="px-5 py-3">
                      <span className={`inline-block text-xs px-2 py-0.5 rounded-full ${u.role === "CEO" ? "bg-primary/10 text-primary" : "bg-emerald-100 text-emerald-700"}`}>
                        {u.role}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-right">
                      <button onClick={() => { setResetting(u); setNewPassword(""); }} className="p-2 rounded-lg hover:bg-secondary" data-testid={`user-reset-${u.id}`}>
                        <KeyRound size={14} />
                      </button>
                      <button onClick={() => remove(u.id)} className="p-2 rounded-lg hover:bg-destructive/10 text-destructive ml-1" data-testid={`user-del-${u.id}`}>
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
                {!loading && users.length === 0 ? (
                  <tr><td colSpan={4} className="text-center py-10 text-muted-foreground">No users yet.</td></tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </div>
      </main>

      <Dialog open={open} onOpenChange={(v) => !v && setOpen(false)}>
        <DialogContent className="max-w-md" data-testid="user-dialog">
          <DialogTitle>Create user</DialogTitle>
          <div className="space-y-3 mt-3">
            <Field label="Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testId="user-name" />
            <Field label="Email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testId="user-email" />
            <Field label="Password" type="password" value={form.password} onChange={(v) => setForm({ ...form, password: v })} testId="user-password" />
            <div>
              <label className="text-sm font-semibold mb-1.5 block">Role</label>
              <select
                data-testid="user-role"
                value={form.role}
                onChange={(e) => setForm({ ...form, role: e.target.value })}
                className="w-full px-3 py-2.5 rounded-lg border border-input bg-white focus:outline-none focus:ring-2 focus:ring-primary/40"
              >
                <option value="MANAGER">Manager</option>
                <option value="CEO">CEO</option>
              </select>
            </div>
          </div>
          <div className="mt-5 flex gap-2 justify-end">
            <button onClick={() => setOpen(false)} className="px-4 py-2 rounded-lg border border-border hover:bg-secondary text-sm font-semibold">Cancel</button>
            <button onClick={create} data-testid="user-save" className="px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-semibold hover:brightness-110">Create</button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!resetting} onOpenChange={(v) => !v && setResetting(null)}>
        <DialogContent className="max-w-md" data-testid="reset-dialog">
          <DialogTitle>Reset password for {resetting?.name}</DialogTitle>
          <div className="mt-3">
            <Field label="New password" type="password" value={newPassword} onChange={setNewPassword} testId="reset-password" />
          </div>
          <div className="mt-5 flex gap-2 justify-end">
            <button onClick={() => setResetting(null)} className="px-4 py-2 rounded-lg border border-border text-sm font-semibold">Cancel</button>
            <button onClick={doReset} data-testid="reset-save" className="px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-semibold">Reset</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", testId }) {
  return (
    <div>
      <label className="text-sm font-semibold mb-1.5 block">{label}</label>
      <input
        data-testid={testId}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2.5 rounded-lg border border-input bg-white focus:outline-none focus:ring-2 focus:ring-primary/40"
      />
    </div>
  );
}
